import { isOtherOption } from '@/shared-project';
import type {
  MessageQuestionOptionV2,
  Maybe,
  QuestionOptionAssetModel,
} from '@/survey-graphql';

const sessionShuffledOptionsCache = new Map<
  string,
  MessageQuestionOptionV2[]
>();

function cacheKey(sessionId: string, questionId: string): string {
  return `${sessionId}:${questionId}`;
}

/** djb2 string hash for deterministic seed from sessionId:questionId (same input => same order on server and client). */
function hashStringToSeed(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/** Seeded PRNG returning [0, 1). Same seed => same sequence. */
function createSeededRng(seed: number): () => number {
  let s = seed || 1;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s >>> 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher-Yates shuffle with seeded RNG so order is deterministic for SSR/hydration. */
function shuffleWithSeed<T>(array: T[], seed: number): T[] {
  const rng = createSeededRng(seed);
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export type ShuffleOptionInput = {
  value: string;
  label: string;
  assets?: Maybe<Array<QuestionOptionAssetModel>>;
};

/**
 * Returns options in session-stable order: when randomize is true, shuffles once
 * per session and caches by sessionId + questionId. Uses deterministic shuffle
 * (seeded by sessionId:questionId) so server and client render the same order and
 * hydration succeeds. The "Other" / "Others" option (value contains "other") is
 * always kept last when randomize is true; if multiple match, the last occurrence
 * is kept at the end.
 */
export function getOrCreateShuffledOptions(
  sessionId: string,
  questionId: string,
  options: ReadonlyArray<ShuffleOptionInput>,
  randomize: boolean
): MessageQuestionOptionV2[] | undefined {
  if (!options?.length) return undefined;
  const mapped: MessageQuestionOptionV2[] = options.map((o) => ({
    value: o.value,
    label: o.label,
    assets: o.assets,
  }));
  if (!randomize) return mapped;
  const key = cacheKey(sessionId, questionId);
  const cached = sessionShuffledOptionsCache.get(key);
  if (cached) return cached;
  const seed = hashStringToSeed(key);
  const lastOtherIndex = mapped
    .map((o, i) => (isOtherOption(o) ? i : -1))
    .reduce((a, i) => (i >= 0 ? i : a), -1);
  const toShuffle =
    lastOtherIndex < 0
      ? mapped
      : [
          ...mapped.slice(0, lastOtherIndex),
          ...mapped.slice(lastOtherIndex + 1),
        ];
  const otherOption = lastOtherIndex < 0 ? null : mapped[lastOtherIndex];
  const shuffled = shuffleWithSeed(toShuffle, seed);
  const result = otherOption ? [...shuffled, otherOption] : shuffled;
  sessionShuffledOptionsCache.set(key, result);
  return result;
}

/**
 * Returns cached shuffled options for a question when reading from insightData,
 * so the order does not reset after the first submit.
 */
export function getCachedShuffledOptions(
  sessionId: string,
  questionId: string
): MessageQuestionOptionV2[] | undefined {
  return sessionShuffledOptionsCache.get(cacheKey(sessionId, questionId));
}
